import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Box from "@mui/material/Box";
import * as SDCCore from "@scandit/web-datacapture-core";
import * as SDCBarcode from "@scandit/web-datacapture-barcode";
import { enabledSymbologies, getScanningArea } from "../../../constants/scandit";
import useAuth from "../../../hooks/useAuth";
import CloseCameraButton from "../../CloseCameraButton";
import ScanditContainer from "./ScanditContainer";
import TorchButton from "./TorchButton";

const findActiveVideoTrack = () => {
  try {
    const host = document.getElementById("scandit-barcode-picker");
    if (!host) return null;
    const video = host.querySelector("video");
    const stream = video && video.srcObject;
    const track = stream && stream.getVideoTracks && stream.getVideoTracks()[0];
    return track || null;
  } catch {
    return null;
  }
};

const styles = {
  scandit: {
    position: "absolute",
    width: "100%",
    height: "100%",
    touchAction: "none",
  },
};

let dataCaptureContext = null;
let initializationPromise = null;
let activeCameraInitialization = null;
const SCANDIT_WEB_SDK_VERSION = "8.2.0";

export async function configureScandit(license) {
  if (dataCaptureContext) {
    return;
  }

  if (initializationPromise) {
    await initializationPromise;
    return;
  }

  initializationPromise = (async () => {
    try {
      dataCaptureContext = await SDCCore.DataCaptureContext.forLicenseKey(license, {
        libraryLocation: `https://cdn.jsdelivr.net/npm/@scandit/web-datacapture-barcode@${SCANDIT_WEB_SDK_VERSION}/sdc-lib/`,
        moduleLoaders: [SDCBarcode.barcodeCaptureLoader()],
      });
    } finally {
      initializationPromise = null;
    }
  })();

  await initializationPromise;
}

export default function ScanditScanner({ onDetected, onClose }) {
  const barcodeCaptureRef = useRef();
  const viewRef = useRef();
  const cameraRef = useRef();
  const onDetectedRef = useRef(onDetected);
  const [torchOn, setTorchOn] = useState(false);
  const [zoomCaps, setZoomCaps] = useState({ supported: false, min: 1, max: 10, step: 0.1 });
  const [currentZoom, setCurrentZoom] = useState(1);
  const { user } = useAuth();

  useEffect(() => {
    onDetectedRef.current = onDetected;
  }, [onDetected]);

  const applyZoomFactor = async (nextZoom) => {
    if (!cameraRef.current) {
      return;
    }
    try {
      const camera = cameraRef.current;
      const updated = new SDCCore.CameraSettings(camera.settings);
      const clamped = Math.max(1, Math.min(nextZoom, zoomCaps.max || nextZoom));
      updated.zoomFactor = clamped;
      await camera.applySettings(updated);
      setCurrentZoom(clamped);
      try {
        const track = findActiveVideoTrack();
        const caps = track?.getCapabilities?.();
        if (caps && typeof caps.zoom !== "undefined") {
          await track.applyConstraints({ advanced: [{ zoom: clamped }] });
        }
      } catch (err) {
        // ignore
      }
    } catch (e) {
      // ignore
    }
  };

  const toggleTorch = async () => {
    if (cameraRef.current) {
      const newTorchState = torchOn ? SDCCore.TorchState.Off : SDCCore.TorchState.On;
      await cameraRef.current.setDesiredTorchState(newTorchState);
      setTorchOn(!torchOn);
    }
  };

  const handleClose = async () => {
    if (barcodeCaptureRef.current) {
      try {
        await barcodeCaptureRef.current.setEnabled(false);
      } catch (e) {
        // ignore
      }
    }
    if (cameraRef.current) {
      try {
        await cameraRef.current.setDesiredTorchState(SDCCore.TorchState.Off);
      } catch {}
      try {
        await cameraRef.current.switchToDesiredState(SDCCore.FrameSourceState.Off);
      } catch (e) {
        // ignore
      }
    }
    onClose();
  };

  useEffect(() => {
    let mounted = true;

    const initializeScanner = async () => {
      try {
        if (activeCameraInitialization) {
          return;
        }
        
        activeCameraInitialization = true;
        
        const scandidLicense = user?.settings?.scandid_license;
        if (!scandidLicense) {
          activeCameraInitialization = null;
          return;
        }

        await configureScandit(scandidLicense);
        const camera = SDCCore.Camera.pickBestGuess();
        cameraRef.current = camera;

        const cameraSettings = new SDCCore.CameraSettings();
        cameraSettings.preferredResolution = SDCCore.VideoResolution.HD;
        cameraSettings.focusGestureStrategy = SDCCore.FocusGestureStrategy.ManualUntilCapture;
        cameraSettings.zoomGestureZoomFactor = 2;
        cameraSettings.zoomFactor = 1;
        await camera.applySettings(cameraSettings);

        await dataCaptureContext.setFrameSource(camera);

        const settings = new SDCBarcode.BarcodeCaptureSettings();
        settings.enableSymbologies(enabledSymbologies);
        settings.codeDuplicateFilter = 1000;
        settings
          .settingsForSymbology(SDCBarcode.Symbology.EAN13UPCA)
          .setExtensionEnabled("remove_leading_upca_zero", true);

        // Restrict scanning to only the viewfinder area
        // Use dynamic scanning area based on viewport size for better QR code detection on mobile
        const scanningArea = getScanningArea();
        const locationSelection = SDCCore.RectangularLocationSelection.withWidthAndAspectRatio(
          new SDCCore.NumberWithUnit(scanningArea.width, SDCCore.MeasureUnit.Fraction),
          scanningArea.height / scanningArea.width
        );
        settings.locationSelection = locationSelection;

        const barcodeCapture = await SDCBarcode.BarcodeCapture.forContext(dataCaptureContext, settings);
        barcodeCaptureRef.current = barcodeCapture;

        barcodeCapture.addListener({
          didScan: async (barcodeCapture, session) => {
            try {
              if (!mounted) {
                return;
              }
              const code = session?.newlyRecognizedBarcode || null;
              if (!code) return;
              const barcode = code;
              const value = typeof barcode?.data === "string" ? barcode.data.trim() : "";
              await barcodeCapture.setEnabled(false);
              try {
                await Promise.resolve(onDetectedRef.current?.(value));
              } catch (cbErr) {
                // ignore
              }
              handleClose();
            } catch (err) {
              // ignore
            }
          },
        });

        const view = await SDCCore.DataCaptureView.forContext(dataCaptureContext);
        viewRef.current = view;
        view.connectToElement(document.getElementById("scandit-barcode-picker"));
        // Disable built-in swipe-to-zoom; slider-only UI
        view.zoomGesture = null;
        // Attempt to log underlying media track capabilities once the video element is attached
        setTimeout(() => {
          try {
            const track = findActiveVideoTrack();
            const caps = track?.getCapabilities?.();
            if (caps && typeof caps.zoom !== "undefined") {
              setZoomCaps({
                supported: true,
                min: typeof caps.zoom.min === "number" ? Math.max(1, caps.zoom.min) : 1,
                max: typeof caps.zoom.max === "number" ? Math.max(1, caps.zoom.max) : 10,
                step: typeof caps.zoom.step === "number" ? caps.zoom.step : 0.1,
              });
            } else {
              setZoomCaps({ supported: false, min: 1, max: 10, step: 0.1 });
            }
          } catch (_) {}
        }, 300);

        const overlay = await SDCBarcode.BarcodeCaptureOverlay.withBarcodeCaptureForView(barcodeCapture, view);

        // Add rectangular viewfinder (target area)
        // Viewfinder size matches the scanning area for accurate user feedback
        const viewfinder = new SDCCore.RectangularViewfinder(
          SDCCore.RectangularViewfinderStyle.Rounded,
          SDCCore.RectangularViewfinderLineStyle.Bold
        );
        const viewfinderSize = new SDCCore.SizeWithUnit(
          new SDCCore.NumberWithUnit(scanningArea.width, SDCCore.MeasureUnit.Fraction),
          new SDCCore.NumberWithUnit(scanningArea.height, SDCCore.MeasureUnit.Fraction)
        );
        viewfinder.setSize(viewfinderSize);
        viewfinder.dimming = 0.3;
        viewfinder.color = SDCCore.Color.fromHex("#007AFF");

        await overlay.setViewfinder(viewfinder);

        await view.addOverlay(overlay);

        await barcodeCapture.setEnabled(true);

        await camera.switchToDesiredState(SDCCore.FrameSourceState.On);
        
        activeCameraInitialization = null;
      } catch (error) {
        console.error("Failed to initialize Scandit scanner:", error);
        activeCameraInitialization = null;
      }
    };

    initializeScanner();

    return () => {
      mounted = false;
      if (cameraRef.current) {
        cameraRef.current.switchToDesiredState(SDCCore.FrameSourceState.Off).catch(() => {});
      }
    };
  }, []);

  return createPortal(
    <ScanditContainer>
      <Box id="scandit-barcode-picker" sx={styles.scandit} />
      <Box>
        <CloseCameraButton onClick={handleClose} />
      </Box>
      <TorchButton torchOn={torchOn} onClick={toggleTorch} />
      <Box position="fixed" bottom={140} left={0} right={0} zIndex={99} display="flex" justifyContent="center" alignItems="center" sx={{ gap: 2 }}>
        <Box
          onClick={() => applyZoomFactor(1)}
          sx={{
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: 500,
            color: currentZoom === 1 ? "#FFD60A" : "rgba(255, 255, 255, 0.6)",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "color 0.2s ease",
            userSelect: "none",
          }}
        >
          1×
        </Box>
        <Box
          onClick={() => applyZoomFactor(2)}
          sx={{
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: 500,
            color: currentZoom === 2 ? "#FFD60A" : "rgba(255, 255, 255, 0.6)",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "color 0.2s ease",
            userSelect: "none",
          }}
        >
          2×
        </Box>
        <Box
          onClick={() => applyZoomFactor(5)}
          sx={{
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: 500,
            color: currentZoom === 5 ? "#FFD60A" : "rgba(255, 255, 255, 0.6)",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "color 0.2s ease",
            userSelect: "none",
          }}
        >
          5×
        </Box>
        <Box
          onClick={() => applyZoomFactor(10)}
          sx={{
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: 500,
            color: currentZoom === 10 ? "#FFD60A" : "rgba(255, 255, 255, 0.6)",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            borderRadius: "50%",
            width: "32px",
            height: "32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "color 0.2s ease",
            userSelect: "none",
          }}
        >
          10×
        </Box>
      </Box>
    </ScanditContainer>,
    document.body
  );
}
